import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useApi, useConsulta, useSubida } from '@plataforma/web/api';
import { aBase64, extraerDtc } from '../dtc';
import { ETIQUETAS_ESTADO, fechaCorta } from '../formato';
import { usePuede } from '../permisos';
import { nombreVehiculo } from './Tablero';
import type { Vehiculo } from './tipos';

/** Inventario de vehículos por VIN, con filtro por etapa. */
export function Vehiculos() {
  const permitido = usePuede();
  const [params, setParams] = useSearchParams();
  const estado = params.get('estado') ?? '';
  const deBaja = params.get('baja') === 'true';
  const consulta = new URLSearchParams({
    ...(estado && { estado }),
    ...(deBaja && { baja: 'true' }),
  }).toString();
  const { datos, error } = useConsulta<Vehiculo[]>(
    `/inventory/vehiculos${consulta ? `?${consulta}` : ''}`,
  );
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Vehículos</h1>
          <div className="subtitulo">Cada auto por su VIN, desde la subasta hasta la venta</div>
        </div>
        <div className="relleno" />
        <label className="campo">
          <span>Etapa</span>
          <select
            value={estado}
            onChange={(e) => setParams(e.target.value ? { estado: e.target.value } : {})}
          >
            <option value="">Todas</option>
            {Object.entries(ETIQUETAS_ESTADO).map(([clave, texto]) => (
              <option key={clave} value={clave}>
                {texto}
              </option>
            ))}
          </select>
        </label>
        <Link className="boton" to={deBaja ? '/vehiculos' : '/vehiculos?baja=true'}>
          {deBaja ? 'Ver inventario' : 'Ver dados de baja'}
        </Link>
        {permitido('vehiculos.registrar') && (
          <Link className="boton primario" to="/vehiculos/nuevo">
            Registrar vehículo
          </Link>
        )}
      </div>
      {deBaja && (
        <p className="subtitulo">
          Vehículos dados de baja: no cuentan en el inventario; se pueden reactivar desde su ficha.
        </p>
      )}
      {error && <p className="error">{error}</p>}
      <section className="seccion">
        {datos && !datos.length ? (
          <p className="vacio">
            {deBaja
              ? 'No hay vehículos dados de baja.'
              : `No hay vehículos ${estado ? 'en esta etapa' : 'todavía'}.`}
          </p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Vehículo</th>
                <th>Título</th>
                <th>Etapa</th>
                <th className="num">Kilometraje</th>
                <th>Alta</th>
              </tr>
            </thead>
            <tbody>
              {(datos ?? []).map((v) => (
                <tr key={v.id}>
                  <td>
                    <Link to={`/vehiculos/${v.id}`}>{nombreVehiculo(v)}</Link>
                    <div className="vin">{v.vin}</div>
                  </td>
                  <td>
                    <span className={`etiqueta ${v.titulo === 'salvage' ? 'aviso' : ''}`}>
                      {v.titulo}
                    </span>
                  </td>
                  <td>{ETIQUETAS_ESTADO[v.estado] ?? v.estado}</td>
                  <td className="num">
                    {v.kilometraje !== null
                      ? `${v.kilometraje.toLocaleString('es-MX')} ${v.unidadKilometraje}`
                      : '—'}
                  </td>
                  <td>{fechaCorta(v.creadoEn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/** Resultado de los pasos después del alta (fotos, reporte y estimaciones), para la ficha. */
export interface AvisosAlta {
  avisos: string[];
}

const FOTOS_IA = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Alta por VIN (Inventario lo decodifica con NHTSA vPIC). Si hay fotos o el reporte del escáner,
 * se guardan como documentos del vehículo y, si se pide, se estiman la reparación por fotos (IA)
 * y por códigos DTC; las estimaciones quedan por aprobar en la ficha.
 */
export function NuevoVehiculo() {
  const api = useApi();
  const subir = useSubida();
  const navegar = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [paso, setPaso] = useState<string | null>(null);
  const [codigos, setCodigos] = useState<string[]>([]);

  const leerReporte = async (archivo: File | undefined) => {
    if (!archivo) return;
    if (archivo.type === 'application/pdf') return; // El PDF se guarda; los códigos se capturan.
    const leidos = extraerDtc(await archivo.text());
    setCodigos((c) => [...new Set([...c, ...leidos])]);
  };

  const enviar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const km = String(f.get('kilometraje') ?? '').trim();
    const fotos = (f.getAll('fotos') as File[]).filter((a) => a.size > 0);
    const reporte = f.get('reporte') as File | null;
    const dtc = [...new Set([...codigos, ...extraerDtc(String(f.get('codigos') ?? ''))])];
    setError(null);
    setPaso('Registrando el vehículo…');
    let v: Vehiculo;
    try {
      v = await api<Vehiculo>('POST', '/inventory/vehiculos', {
        vin: String(f.get('vin') ?? ''),
        titulo: f.get('titulo'),
        estado: f.get('estado'),
        ...(km && { kilometraje: Number(km) }),
        unidadKilometraje: f.get('unidad'),
        danos: String(f.get('danos') ?? '')
          .split(',')
          .map((d) => d.trim())
          .filter(Boolean),
      });
    } catch (err) {
      setError((err as Error).message);
      setPaso(null);
      return;
    }
    // El vehículo ya existe: lo que falle después se avisa en la ficha, no se pierde el alta.
    const avisos: string[] = [];
    const datosVehiculo = {
      vehiculoId: v.id,
      vin: v.vin,
      ...(v.anio && { anio: v.anio }),
      ...(v.marca && { marca: v.marca }),
      ...(v.modelo && { modelo: v.modelo }),
    };
    for (const [n, foto] of fotos.entries()) {
      setPaso(`Subiendo foto ${n + 1} de ${fotos.length}…`);
      await subir(
        `/inventory/vehiculos/${v.id}/documentos?tipo=foto&nombre=${encodeURIComponent(foto.name)}`,
        foto,
      ).catch((err: Error) => avisos.push(`Foto ${foto.name}: ${err.message}`));
    }
    if (reporte && reporte.size > 0 && reporte.type === 'application/pdf') {
      setPaso('Guardando el reporte del escáner…');
      await subir(
        `/inventory/vehiculos/${v.id}/documentos?tipo=inspeccion&nombre=${encodeURIComponent(reporte.name)}`,
        reporte,
      ).catch((err: Error) => avisos.push(`Reporte ${reporte.name}: ${err.message}`));
    }
    if (dtc.length) {
      setPaso('Estimando por códigos DTC…');
      await api('POST', '/workshop/diagnosticos', {
        ...datosVehiculo,
        codigos: dtc,
        ...(km && { kilometraje: Number(km) }),
      }).catch((err: Error) => avisos.push(`Estimación por DTC: ${err.message}`));
    }
    const paraIa = fotos
      .filter((a) => FOTOS_IA.includes(a.type) && a.size <= 5 * 1024 * 1024)
      .slice(0, 8);
    if (f.get('estimarFotos') === 'on' && paraIa.length) {
      setPaso('Estimando la reparación por fotos (IA)…');
      try {
        await api('POST', '/workshop/estimaciones/fotos', {
          ...datosVehiculo,
          ...(f.get('danos') && { danoDeclarado: String(f.get('danos')).slice(0, 300) }),
          fotos: await Promise.all(
            paraIa.map(async (a) => ({
              tipoContenido: a.type,
              base64: await aBase64(a),
              nombre: a.name.slice(0, 120),
            })),
          ),
        });
      } catch (err) {
        avisos.push(`Estimación por fotos: ${(err as Error).message}`);
      }
    }
    void navegar(`/vehiculos/${v.id}`, { state: { avisos } satisfies AvisosAlta });
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Registrar vehículo</h1>
          <div className="subtitulo">Con el VIN se decodifican año, marca, modelo y planta</div>
        </div>
      </div>
      <section className="seccion">
        <form className="cuerpo formulario" onSubmit={(e) => void enviar(e)}>
          <label className="campo">
            <span>VIN</span>
            <input
              name="vin"
              required
              minLength={17}
              maxLength={17}
              autoCapitalize="characters"
              placeholder="17 caracteres"
            />
          </label>
          <label className="campo">
            <span>Título</span>
            <select name="titulo" defaultValue="salvage">
              <option value="limpio">Limpio</option>
              <option value="salvage">Salvage</option>
              <option value="rebuilt">Rebuilt</option>
              <option value="otro">Otro</option>
            </select>
          </label>
          <label className="campo">
            <span>Situación</span>
            <select name="estado" defaultValue="comprado">
              <option value="lote_en_subasta">Lo estoy vigilando en subasta</option>
              <option value="comprado">Ya lo compré</option>
              <option value="recibido">Ya está en mi lote</option>
            </select>
          </label>
          <label className="campo">
            <span>Kilometraje</span>
            <input name="kilometraje" type="number" min={0} inputMode="numeric" />
          </label>
          <label className="campo">
            <span>Unidad</span>
            <select name="unidad" defaultValue="mi">
              <option value="mi">Millas</option>
              <option value="km">Kilómetros</option>
            </select>
          </label>
          <label className="campo">
            <span>Daños (separados por coma)</span>
            <input name="danos" placeholder="Frente, bolsas de aire" />
          </label>
          <label className="campo" style={{ gridColumn: '1 / -1' }}>
            <span>Fotos de la subasta o del lote (opcional)</span>
            <input
              name="fotos"
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/heic"
            />
          </label>
          <label
            className="campo"
            style={{ flexDirection: 'row', alignItems: 'center', gridColumn: '1 / -1' }}
          >
            <input name="estimarFotos" type="checkbox" defaultChecked style={{ minHeight: 0 }} />
            <span>
              Estimar la reparación con IA a partir de las fotos (hasta 8; la revisas antes de que
              cuente)
            </span>
          </label>
          <label className="campo">
            <span>Reporte del escáner (TXT, CSV o PDF; opcional)</span>
            <input
              name="reporte"
              type="file"
              accept=".txt,.csv,text/plain,text/csv,application/pdf"
              onChange={(e) => void leerReporte(e.target.files?.[0])}
            />
          </label>
          <label className="campo">
            <span>Códigos DTC (si no vienen en el archivo)</span>
            <input name="codigos" placeholder="P0300, P0171" />
          </label>
          {codigos.length > 0 && (
            <p className="subtitulo" style={{ gridColumn: '1 / -1' }}>
              Códigos leídos del reporte: {codigos.join(', ')}
            </p>
          )}
          {error && (
            <p className="error" style={{ gridColumn: '1 / -1' }}>
              {error}
            </p>
          )}
          <div className="acciones">
            {paso && <span className="subtitulo">{paso}</span>}
            <Link className="boton" to="/vehiculos">
              Cancelar
            </Link>
            <button className="boton primario" disabled={paso !== null}>
              Registrar
            </button>
          </div>
        </form>
      </section>
    </>
  );
}
